// SVG to JSX / React component, for /svg-to-react. Runs in the browser on the visitor's SVG.
//
// The SVG is parsed with DOMParser and the tree is printed as JSX:
// - attribute names become React prop names (class -> className, stroke-width -> strokeWidth,
//   xml:space -> xmlSpace), using React's own list of SVG attribute aliases;
// - style strings become style objects;
// - xlink:href becomes href;
// - ids that the SVG refers to (url(#id), href="#id", aria-labelledby, <style>) can be made
//   unique per component instance with useId(), so two copies on one page don't share
//   gradients or clip paths;
// - scripts, on* handlers, javascript: URLs, comments and editor metadata are dropped.

import {
  MATHML_NS,
  SVG_NS,
  XHTML_NS,
  XLINK_NS,
  XML_NS,
  XMLNS_NS,
  inspectSvg,
  isJavascriptUrl,
  lengthToPx,
  normalizeSvg,
  parseSvg,
} from '@/utils/svg-tools/svg-source';

export interface JsxOptions {
  componentName: string;
  typescript: boolean;
  /** Prefix referenced ids with useId() so every instance has its own. */
  uniqueIds: boolean;
  /** Drop width and height from the root so CSS or props size the icon. */
  removeDimensions: boolean;
}

export type JsxResult = { ok: true; code: string; notes: string[] } | { ok: false; error: string };

export const defaultJsxOptions: JsxOptions = {
  componentName: 'SvgIcon',
  typescript: true,
  uniqueIds: true,
  removeDimensions: false,
};

/** "my-logo (2).svg" -> "MyLogo2". Always a valid component name. */
export function toComponentName(input: string): string {
  const words = input
    .replace(/\.svgz?$/i, '')
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean);
  let name = words.map((w) => w[0].toUpperCase() + w.slice(1)).join('');
  if (!name) return defaultJsxOptions.componentName;
  if (/^\d/.test(name)) name = `Svg${name}`;
  return name;
}

// SVG attributes React spells in camelCase (react-dom's alias table). The kebab-case name is
// derived from each entry. transform-origin is missing on purpose: React's TypeScript types
// don't list transformOrigin as an attribute, so it is moved into the style object instead.
const CAMEL_ATTRS =
  'accentHeight alignmentBaseline arabicForm baselineShift capHeight clipPath clipRule colorInterpolation ' +
  'colorInterpolationFilters colorProfile colorRendering dominantBaseline fillOpacity fillRule floodColor ' +
  'floodOpacity fontFamily fontSize fontSizeAdjust fontStretch fontStyle fontVariant fontWeight glyphName ' +
  'glyphOrientationHorizontal glyphOrientationVertical horizAdvX horizOriginX imageRendering letterSpacing ' +
  'lightingColor markerEnd markerMid markerStart overlinePosition overlineThickness paintOrder pointerEvents ' +
  'renderingIntent shapeRendering stopColor stopOpacity strikethroughPosition strikethroughThickness ' +
  'strokeDasharray strokeDashoffset strokeLinecap strokeLinejoin strokeMiterlimit strokeOpacity strokeWidth ' +
  'textAnchor textDecoration textRendering underlinePosition underlineThickness unicodeBidi unicodeRange ' +
  'unitsPerEm vAlphabetic vHanging vIdeographic vMathematical vectorEffect vertAdvY vertOriginX vertOriginY ' +
  'wordSpacing writingMode xHeight';

const ATTR_ALIASES: Map<string, string> = new Map([
  ...CAMEL_ATTRS.split(' ').map((camel): [string, string] => [camel.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`), camel]),
  ['class', 'className'],
  ['for', 'htmlFor'],
  ['tabindex', 'tabIndex'],
  ['crossorigin', 'crossOrigin'],
]);

// Style properties the React CSSProperties type knows for sure. Anything else (Inkscape's
// shape-inside, old -inkscape-* names) makes the TSX output cast the object to CSSProperties.
const KNOWN_STYLE =
  new Set(
    ('alignmentBaseline baselineShift clip clipPath clipRule color colorInterpolation colorInterpolationFilters ' +
      'colorRendering cursor direction display dominantBaseline fill fillOpacity fillRule filter floodColor ' +
      'floodOpacity font fontFamily fontFeatureSettings fontKerning fontSize fontSizeAdjust fontStretch fontStyle ' +
      'fontVariant fontVariantCaps fontVariantLigatures fontVariantNumeric fontVariationSettings fontWeight ' +
      'imageRendering isolation letterSpacing lightingColor lineHeight marker markerEnd markerMid markerStart mask ' +
      'maskType mixBlendMode opacity overflow paintOrder pointerEvents shapeRendering stopColor stopOpacity stroke ' +
      'strokeDasharray strokeDashoffset strokeLinecap strokeLinejoin strokeMiterlimit strokeOpacity strokeWidth ' +
      'textAnchor textDecoration textDecorationColor textDecorationLine textDecorationStyle textIndent ' +
      'textRendering textTransform transform transformBox transformOrigin unicodeBidi vectorEffect visibility ' +
      'whiteSpace wordSpacing writingMode inlineSize textAlign').split(' ')
  );

const TEXT_PARENTS = new Set(['text', 'tspan', 'textPath', 'title', 'desc', 'foreignObject']);

class Notes {
  counts = new Map<string, number>();
  names = new Map<string, Set<string>>();
  add(key: string, n = 1) {
    this.counts.set(key, (this.counts.get(key) || 0) + n);
  }
  name(key: string, value: string) {
    if (!this.names.has(key)) this.names.set(key, new Set());
    this.names.get(key)!.add(value);
    this.add(key);
  }
  n(key: string) {
    return this.counts.get(key) || 0;
  }
  list(key: string) {
    return Array.from(this.names.get(key) || []);
  }
}

function camelStyleProp(prop: string): string {
  if (prop.startsWith('--')) return prop;
  const p = prop.toLowerCase();
  const head = p.startsWith('-ms-') ? p.slice(1) : p;
  return head.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase());
}

/** Split a style attribute into declarations, respecting quotes and parentheses. */
export function parseStyle(style: string): [string, string][] {
  const out: [string, string][] = [];
  let depth = 0;
  let quote: string | null = null;
  let start = 0;
  const push = (end: number) => {
    const decl = style.slice(start, end);
    const colon = decl.indexOf(':');
    if (colon > 0) {
      const prop = decl.slice(0, colon).trim();
      const value = decl.slice(colon + 1).trim();
      if (prop && value) out.push([prop, value]);
    }
  };
  for (let i = 0; i < style.length; i++) {
    const c = style[i];
    if (quote) {
      if (c === '\\') i++;
      else if (c === quote) quote = null;
    } else if (c === '"' || c === "'") quote = c;
    else if (c === '(') depth++;
    else if (c === ')') depth = Math.max(0, depth - 1);
    else if (c === ';' && depth === 0) {
      push(i);
      start = i + 1;
    }
  }
  push(style.length);
  return out;
}

const UID = '\u0000uid\u0000';

/** A JS expression for a string that may contain the useId placeholder. */
function jsString(value: string): string {
  if (!value.includes(UID)) return JSON.stringify(value);
  const body = value
    .split(UID)
    .map((part) => part.replace(/\\/g, '\\\\').replace(/`/g, '\\`').replace(/\$\{/g, '\\${'))
    .join('${uid}');
  return `\`${body}\``;
}

/** A JSX attribute: name="value" when that is safe, name={...} otherwise. */
function jsxAttr(name: string, value: string): string {
  if (!value.includes(UID) && !/["&\n\r\t]/.test(value)) return `${name}="${value}"`;
  return `${name}={${jsString(value)}}`;
}

function jsxText(text: string): string {
  if (/[{}<>&]/.test(text) || /^\s|\s$/.test(text) || /[\n\r]/.test(text)) return `{${JSON.stringify(text)}}`;
  return text;
}

const IDENT = /^[A-Za-z_$][\w$]*$/;

/**
 * Apply fn to the selector (and at-rule) text of a stylesheet and leave the declarations
 * alone, so a color such as #abc is never mistaken for an id.
 */
function mapSelectors(css: string, fn: (selector: string) => string): string {
  let out = '';
  let start = 0;
  for (let i = 0; i < css.length; i++) {
    const c = css[i];
    if (c === '{') {
      out += `${fn(css.slice(start, i))}{`;
      start = i + 1;
    } else if (c === '}' || c === ';') {
      out += css.slice(start, i + 1);
      start = i + 1;
    }
  }
  return out + css.slice(start);
}

/** Ids the SVG points at from inside itself. */
function referencedIds(root: Element): Set<string> {
  const refs = new Set<string>();
  const urlRe = /url\(\s*['"]?#([^'")\s]+)['"]?\s*\)/g;
  for (const el of [root, ...Array.from(root.getElementsByTagName('*'))]) {
    for (const attr of Array.from(el.attributes)) {
      for (const m of attr.value.matchAll(urlRe)) refs.add(m[1]);
      if (attr.localName === 'href' && attr.value.startsWith('#')) refs.add(attr.value.slice(1));
      if (attr.localName === 'aria-labelledby' || attr.localName === 'aria-describedby') {
        attr.value.split(/\s+/).filter(Boolean).forEach((id) => refs.add(id));
      }
    }
    if (el.localName === 'style') {
      const css = el.textContent || '';
      for (const m of css.matchAll(urlRe)) refs.add(m[1]);
      mapSelectors(css, (selector) => {
        for (const m of selector.matchAll(/#([A-Za-z_][\w-]*)/g)) refs.add(m[1]);
        return selector;
      });
    }
  }
  return refs;
}

function escapeRe(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function svgToJsx(source: string, options: JsxOptions): JsxResult {
  const normalized = normalizeSvg(source);
  const parsed = parseSvg(normalized.text);
  if (!parsed.ok) return parsed;
  const { root } = parsed;
  const opts = { ...options, componentName: toComponentName(options.componentName) };
  const notes = new Notes();

  // Ids to make unique: defined in the SVG and referenced from inside it.
  const defined = new Set(Array.from(root.querySelectorAll('[id]')).map((el) => el.getAttribute('id')!));
  if (root.hasAttribute('id')) defined.add(root.getAttribute('id')!);
  const scoped = new Set<string>();
  if (opts.uniqueIds) {
    for (const id of Array.from(referencedIds(root))) if (defined.has(id)) scoped.add(id);
  }
  // Longest first, so an id that is a prefix of another can't match inside it.
  const scopedList = Array.from(scoped).sort((a, b) => b.length - a.length);
  const urlRefRe = scopedList.length
    ? new RegExp(`(url\\(\\s*['"]?#)(${scopedList.map(escapeRe).join('|')})(?=['"]?\\s*\\))`, 'g')
    : null;
  const cssIdRe = scopedList.length ? new RegExp(`#(${scopedList.map(escapeRe).join('|')})(?![\\w-])`, 'g') : null;
  const scopeRefs = (value: string) => (urlRefRe ? value.replace(urlRefRe, `$1${UID}-$2`) : value);
  const scopeId = (id: string) => (scoped.has(id) ? `${UID}-${id}` : id);

  let castStyle = false;

  /** Entries later in the list win, as later declarations do in CSS. */
  function styleObject(entries: [string, string][]): string | null {
    const props = new Map<string, string>();
    let unknown = false;
    for (const [rawProp, rawValue] of entries) {
      const prop = rawProp.trim();
      const lower = prop.toLowerCase();
      if (lower === 'enable-background') {
        notes.add('enableBackground');
        continue;
      }
      if (lower.startsWith('-inkscape-')) {
        notes.add('editor');
        continue;
      }
      let value = rawValue;
      if (/!\s*important\s*$/i.test(value)) {
        value = value.replace(/\s*!\s*important\s*$/i, '');
        notes.add('important');
      }
      const key = camelStyleProp(prop);
      if (!KNOWN_STYLE.has(key) && !/^(Webkit|Moz|ms)[A-Z]/.test(key)) unknown = true;
      props.set(key, `${IDENT.test(key) ? key : JSON.stringify(key)}: ${jsString(scopeRefs(value))}`);
    }
    if (!props.size) return null;
    const obj = `{ ${Array.from(props.values()).join(', ')} }`;
    if (unknown && opts.typescript) {
      castStyle = true;
      return `{${obj} as CSSProperties}`;
    }
    return `{${obj}}`;
  }

  function attrsOf(el: Element, isRoot: boolean): string[] {
    const out: string[] = [];
    // Attributes moved into the style object, then the style attribute, which overrides them.
    const movedEntries: [string, string][] = [];
    const styleEntries: [string, string][] = [];
    const hasHref = el.hasAttribute('href');
    for (const attr of Array.from(el.attributes)) {
      const ns = attr.namespaceURI;
      const local = attr.localName;
      let name: string;
      if (ns === XMLNS_NS || (!ns && attr.name === 'xmlns')) {
        // Namespace declarations: keep only the SVG one on the root.
        if (isRoot && attr.name === 'xmlns' && attr.value === SVG_NS) out.push(`xmlns="${SVG_NS}"`);
        else if (attr.prefix === 'xmlns' && local !== 'xlink') notes.add('editor');
        continue;
      }
      if (ns === XLINK_NS) {
        if (local === 'href') {
          notes.add('xlink');
          if (hasHref) continue;
          name = 'href';
        } else {
          name = `xlink${local[0].toUpperCase()}${local.slice(1)}`;
        }
      } else if (ns === XML_NS) {
        name = `xml${local[0].toUpperCase()}${local.slice(1)}`;
      } else if (ns || attr.prefix) {
        notes.add('editor');
        continue;
      } else if (/^on/i.test(local)) {
        notes.name('handlers', local.toLowerCase());
        continue;
      } else if (local === 'style') {
        styleEntries.push(...parseStyle(attr.value));
        notes.add('styles');
        continue;
      } else if (local === 'transform-origin') {
        movedEntries.push(['transform-origin', attr.value]);
        continue;
      } else if (local === 'enable-background') {
        notes.add('enableBackground');
        continue;
      } else if (/^(data|aria)-/.test(local)) {
        name = local;
      } else if (!/^[A-Za-z_][\w-]*$/.test(local)) {
        notes.add('editor');
        continue;
      } else {
        name = ATTR_ALIASES.get(local) || local;
        if (name !== local) notes.add('renamed');
      }

      let value = attr.value;
      if (isJavascriptUrl(value)) {
        notes.add('jsLink');
        continue;
      }
      if (isRoot && opts.removeDimensions && (name === 'width' || name === 'height')) continue;
      if (name === 'id') value = scopeId(value);
      else if (name === 'href' && value.startsWith('#') && scoped.has(value.slice(1))) value = `#${UID}-${value.slice(1)}`;
      else if (name === 'aria-labelledby' || name === 'aria-describedby') value = value.split(/\s+/).map(scopeId).join(' ');
      else value = scopeRefs(value);
      out.push(jsxAttr(name, value));
    }
    if (isRoot && opts.removeDimensions && !el.hasAttribute('viewBox')) {
      // Without a viewBox, user units are CSS px, so the box is the size in px.
      const w = lengthToPx(el.getAttribute('width'));
      const h = lengthToPx(el.getAttribute('height'));
      if (w && h) {
        out.push(`viewBox="0 0 ${+w.toFixed(3)} ${+h.toFixed(3)}"`);
        notes.add('addedViewBox');
      }
    }
    if (movedEntries.length || styleEntries.length) {
      const obj = styleObject([...movedEntries, ...styleEntries]);
      if (obj) out.push(`style=${obj}`);
    }
    if (isRoot) out.push('{...props}');
    return out;
  }

  function openTag(name: string, attrs: string[], selfClose: boolean, indent: string): string {
    const end = selfClose ? ' />' : '>';
    const line = `${indent}<${name}${attrs.map((a) => ` ${a}`).join('')}${end}`;
    if (line.length <= 100 || attrs.length <= 1) return line;
    return `${indent}<${name}\n${attrs.map((a) => `${indent}  ${a}`).join('\n')}\n${indent}${selfClose ? '/>' : '>'}`;
  }

  function printStyleElement(el: Element, indent: string): string {
    const css = (el.textContent || '').trim();
    const attrs = attrsOf(el, false);
    if (!css) return openTag('style', attrs, true, indent);
    let body = css.replace(/\\/g, '\\\\').replace(/`/g, '\\`').replace(/\$\{/g, '\\${');
    if (cssIdRe) {
      const re = cssIdRe;
      body = mapSelectors(body, (selector) => selector.replace(re, (_m, id: string) => `#\${uid}-${id}`));
    }
    if (urlRefRe) body = body.replace(urlRefRe, (_m, pre: string, id: string) => `${pre}\${uid}-${id}`);
    return `${openTag('style', attrs, false, indent)}{\`${body}\`}</style>`;
  }

  function print(el: Element, indent: string, inText: boolean, isRoot = false): string | null {
    const ns = el.namespaceURI;
    if (ns && ns !== SVG_NS && ns !== XHTML_NS && ns !== MATHML_NS) {
      notes.add('editor');
      return null;
    }
    const name = el.localName;
    if (name === 'script') {
      notes.add('scripts');
      return null;
    }
    if (name === 'metadata') {
      notes.add('editor');
      return null;
    }
    if (name === 'style') return printStyleElement(el, indent);

    const attrs = attrsOf(el, isRoot);
    const textual = inText || TEXT_PARENTS.has(name);
    const children: string[] = [];
    let onlyText = true;
    for (const node of Array.from(el.childNodes)) {
      if (node.nodeType === 1) {
        const child = print(node as Element, `${indent}  `, textual);
        if (child !== null) {
          children.push(child);
          onlyText = false;
        }
      } else if (node.nodeType === 3 || node.nodeType === 4) {
        const text = node.nodeValue || '';
        if (!text.trim() && !textual) continue;
        if (!text) continue;
        children.push(`${indent}  ${jsxText(text)}`);
      }
    }
    if (!children.length) return openTag(name, attrs, true, indent);
    const open = openTag(name, attrs, false, indent);
    if (onlyText && children.length === 1 && !open.includes('\n')) {
      const single = `${open}${children[0].trim()}</${name}>`;
      if (single.length <= 100) return single;
    }
    return `${open}\n${children.join('\n')}\n${indent}</${name}>`;
  }

  const body = print(root, '    ', false, true)!;

  const ts = opts.typescript;
  const typeImports = ['SVGProps', ...(castStyle ? ['CSSProperties'] : [])].sort();
  const lines: string[] = [];
  if (scoped.size) lines.push('import { useId } from "react";');
  if (ts) lines.push(`import type { ${typeImports.join(', ')} } from "react";`);
  if (lines.length) lines.push('');
  lines.push(`export default function ${opts.componentName}(props${ts ? ': SVGProps<SVGSVGElement>' : ''}) {`);
  if (scoped.size) {
    lines.push('  // A prefix per instance keeps each copy\'s gradients and clip paths separate.');
    lines.push('  const uid = useId().replace(/[^\\w-]/g, "");');
  }
  lines.push('  return (', body, '  );', '}', '');

  const out: string[] = [...normalized.fixes];
  if (notes.n('renamed')) out.push(`Renamed ${notes.n('renamed')} attributes to their React names (class → className, stroke-width → strokeWidth and so on).`);
  if (notes.n('styles')) out.push(`Turned ${notes.n('styles')} style attribute${notes.n('styles') === 1 ? '' : 's'} into style objects.`);
  if (notes.n('xlink')) out.push(`Replaced xlink:href with href (${notes.n('xlink')}).`);
  if (scoped.size) out.push(`Made ${scoped.size} referenced id${scoped.size === 1 ? '' : 's'} unique per instance with useId().`);
  if (notes.n('scripts')) out.push(`Removed ${notes.n('scripts')} <script> element${notes.n('scripts') === 1 ? '' : 's'}.`);
  if (notes.n('handlers')) out.push(`Removed event handler attributes: ${notes.list('handlers').join(', ')}. Pass functions as props instead.`);
  if (notes.n('jsLink')) out.push(`Removed ${notes.n('jsLink') === 1 ? 'a javascript: URL' : `${notes.n('jsLink')} javascript: URLs`}.`);
  if (notes.n('editor')) out.push('Removed editor-only metadata and attributes (Inkscape, Illustrator, Sketch).');
  if (notes.n('enableBackground')) out.push('Removed enable-background, which no browser uses.');
  if (notes.n('important')) out.push('Dropped !important from a style; React style objects ignore it.');
  if (notes.n('addedViewBox')) out.push('Added a viewBox from the width and height, so the SVG still scales.');
  const external = inspectSvg(root).externalRefs;
  if (external) out.push(`Warning: this SVG links to ${external === 1 ? 'an outside file' : `${external} outside files`} (<image> or @import), which will load when the component renders.`);
  return { ok: true, code: lines.join('\n'), notes: out };
}
